import * as React from 'react'
import { useNavigate } from 'react-router-dom'

export type LegalSlug = 'regulamin' | 'polityka-prywatnosci' | 'dpa'

type Props = {
  slug: LegalSlug
}

const TITLES: Record<LegalSlug, string> = {
  'regulamin': 'Regulamin',
  'polityka-prywatnosci': 'Polityka Prywatności',
  'dpa': 'Umowa o przetwarzaniu danych',
}

// Ile czekamy na `load` z ramki, zanim uznamy, że PDF się nie wyrenderował.
// Dokumenty ważą ~130–170 kB, więc nawet na słabym LTE mieszczą się w tym oknie.
const IFRAME_LOAD_TIMEOUT_MS = 6000

// Przeglądarka mobilna (Chrome na Androidzie, a przede wszystkim WebView, w którym
// otwiera się rejestracja z aplikacji) potrafi nie renderować PDF-a w <iframe> —
// użytkownik widzi pustą stronę. To blokujące dla weryfikacji Przelewy24, bo
// regulamin musi być dostępny z ekranu rejestracji.
function supportsInlinePdf(): boolean {
  if (typeof navigator === 'undefined') return true

  // Standardowy sygnał (Chrome 94+, Firefox 100+, Safari 16.4+). Android zwraca tu
  // false — wtedy nie próbujemy nawet montować ramki.
  const { pdfViewerEnabled } = navigator as Navigator & { pdfViewerEnabled?: boolean }
  if (typeof pdfViewerEnabled === 'boolean') return pdfViewerEnabled

  // Starsze silniki: obecność wtyczki PDF w mimeTypes.
  if (navigator.mimeTypes && navigator.mimeTypes.namedItem?.('application/pdf')) return true

  // Nic nie wiemy — próbujemy ramki, a timeout niżej złapie ewentualną pustkę.
  return true
}

export function LegalDocumentPage({ slug }: Props) {
  const pdfUrl = `/legal/${slug}.pdf`
  const navigate = useNavigate()
  const handleBack = () => {
    if (window.history.length > 1) navigate(-1)
    else navigate('/')
  }

  const [inlineFailed, setInlineFailed] = React.useState(() => !supportsInlinePdf())
  const [loaded, setLoaded] = React.useState(false)

  React.useEffect(() => {
    if (inlineFailed || loaded) return
    const timer = window.setTimeout(() => setInlineFailed(true), IFRAME_LOAD_TIMEOUT_MS)
    return () => window.clearTimeout(timer)
  }, [inlineFailed, loaded])

  return (
    <div
      className="legal-document-page"
      style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}
    >
      <div
        style={{
          padding: '0.75rem 1.25rem',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
        }}
      >
        <h1 style={{ margin: 0, fontSize: '1.25rem' }}>{TITLES[slug]}</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {/* Zawsze dostępne, niezależnie od tego czy podgląd się wyrenderował. */}
          <a
            href={pdfUrl}
            download
            style={{
              color: '#2563eb',
              fontSize: '0.95rem',
              textDecoration: 'underline',
              textUnderlineOffset: 2,
            }}
          >
            Pobierz PDF
          </a>
          <button
            type="button"
            onClick={handleBack}
            style={{
              background: 'transparent',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              padding: '0.4rem 0.9rem',
              color: '#1f2937',
              fontSize: '0.95rem',
              cursor: 'pointer',
            }}
          >
            Powrót
          </button>
        </div>
      </div>
      {inlineFailed ? (
        <PdfFallback pdfUrl={pdfUrl} title={TITLES[slug]} />
      ) : (
        <iframe
          src={pdfUrl}
          title={TITLES[slug]}
          onLoad={() => setLoaded(true)}
          onError={() => setInlineFailed(true)}
          style={{ flex: 1, width: '100%', border: 0 }}
        />
      )}
    </div>
  )
}

function PdfFallback({ pdfUrl, title }: { pdfUrl: string; title: string }) {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1rem',
        padding: '2rem 1.5rem',
        textAlign: 'center',
      }}
    >
      <p style={{ margin: 0, color: '#6b7280', maxWidth: '32rem' }}>
        Twoja przeglądarka nie wyświetla dokumentów PDF na stronie. Pobierz dokument
        „{title}", aby się z nim zapoznać.
      </p>
      <a
        href={pdfUrl}
        download
        style={{
          display: 'inline-block',
          background: '#2563eb',
          color: '#fff',
          borderRadius: 8,
          padding: '0.7rem 1.6rem',
          fontSize: '1rem',
          textDecoration: 'none',
        }}
      >
        Pobierz PDF
      </a>
      <a
        href={pdfUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#2563eb', fontSize: '0.9rem' }}
      >
        Otwórz w nowej karcie
      </a>
    </div>
  )
}

export default LegalDocumentPage
